import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { optOutsApi } from "@/api";

export default function OptOuts() {
  const [search, setSearch] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["opt-outs", search],
    queryFn: () => optOutsApi.list(search || undefined),
  });

  const removeMutation = useMutation({
    mutationFn: (contactId: string) => optOutsApi.remove(contactId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["opt-outs"] }),
  });

  const addMutation = useMutation({
    mutationFn: (phone: string) => optOutsApi.addByPhone(phone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opt-outs"] });
      setAddPhone("");
    },
  });

  const handleAdd = () => {
    const phone = addPhone.trim();
    if (!phone) return;
    addMutation.mutate(phone);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">配信停止リスト</h2>

      <div className="flex gap-3 items-end">
        <div>
          <label className="block text-xs text-slate-500 mb-1">電話番号を追加</label>
          <input
            type="tel"
            placeholder="08012345678"
            value={addPhone}
            onChange={(e) => setAddPhone(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            className="px-3 py-2 border rounded-md text-sm w-52"
          />
        </div>
        <button
          onClick={handleAdd}
          disabled={addMutation.isPending || !addPhone.trim()}
          className="px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 disabled:opacity-50"
        >
          {addMutation.isPending ? "追加中..." : "配信停止に追加"}
        </button>
      </div>

      <input
        type="text"
        placeholder="名前 / 電話番号で検索..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="px-3 py-2 border rounded-md text-sm w-64"
      />

      {isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium">名前</th>
                <th className="px-4 py-3 text-left font-medium">電話番号</th>
                <th className="px-4 py-3 text-left font-medium">停止日時</th>
                <th className="px-4 py-3 text-left font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data?.data.map((contact) => (
                <tr key={contact.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">{contact.name}</td>
                  <td className="px-4 py-3 font-mono">{contact.phone}</td>
                  <td className="px-4 py-3">
                    {contact.optOutAt
                      ? new Date(contact.optOutAt).toLocaleString("ja-JP")
                      : "-"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => {
                        if (confirm("配信停止を解除しますか？"))
                          removeMutation.mutate(contact.id);
                      }}
                      className="text-blue-600 hover:underline"
                    >
                      解除
                    </button>
                  </td>
                </tr>
              ))}
              {!data?.data.length && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                    配信停止中の連絡先はありません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
